import { isSameDay, format } from 'date-fns'
import { Clock, Check, AlertCircle, AlertTriangle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { LinkifiedText } from '@/components/ui/linkified-text'
import { formatISODateTime, getMessageChannel } from '@/lib/format-utils'
import type { PendingSendStatus } from '@/hooks/useThreadOrchestration'

/**
 * Shape of a single message this pane reads - supports both the
 * `useThreadMessages` response shape (`message_id`/`sent_at`/`body_plain`)
 * and the embedded-thread shape used elsewhere, e.g. useReservationThread
 * (`id`/`date`/`body`), since callers may feed either.
 */
interface Message {
  id?: string
  message_id?: string
  direction?: string
  sent_at?: string
  date?: string
  body?: string
  body_plain?: string
  bodyPlain?: string
  channel?: string
  source?: string
  from?: string
  /**
   * Present only on a client-synthesized message inserted by an in-flight
   * or recently-completed Send - see useThreadOrchestration's pendingSends.
   * Absent on every real message the backend returns.
   */
  pendingStatus?: PendingSendStatus
  pendingError?: string
  pendingIdempotencyKey?: string
}

interface MessageWithDivider {
  message: Message
  showDivider: boolean
}

interface ThreadPaneProps {
  messages?: Message[]
  guestDisplayName?: string
  /** Retries a `failed`/`ambiguous` pending message with the same
   * idempotencyKey - see useThreadOrchestration's handleResendPendingMessage. */
  onResendPendingMessage?: (idempotencyKey: string) => void
  /** True once the bounded confirmation poll for a `sent` pending message
   * gave up without the canonical record showing up - offers the same
   * "Check again" affordance the suggestion flow uses (isPollExhausted). */
  isSendConfirmationExhausted?: boolean
  onCheckAgainSendConfirmation?: () => void
}

/**
 * Day-divider label for the first message of a new calendar day. `sentAt` may
 * be missing for some messages (see the message-key fallback below this
 * function) - in that case the message simply isn't given a divider rather
 * than guessing a date.
 */
function dayLabel(sentAt?: string) {
  const date = new Date(sentAt ?? '')
  return Number.isNaN(date.getTime()) ? null : format(date, 'EEEE, MMMM d')
}

/**
 * Pairs each message with whether it should be preceded by a day divider.
 * A plain top-level function (not a closure over render state) so its
 * internal running-date accumulator is just an ordinary local variable, not
 * a mutation the render pass itself performs.
 */
function withDayDividers(messages: Message[]): MessageWithDivider[] {
  let previousDate: Date | null = null
  return messages.map(message => {
    const sentAt = message.sent_at || message.date
    const currentDate = sentAt ? new Date(sentAt) : null
    const isValidDate = Boolean(currentDate && !Number.isNaN(currentDate.getTime()))
    const showDivider =
      isValidDate && (!previousDate || !isSameDay(currentDate as Date, previousDate))
    if (isValidDate) previousDate = currentDate
    return { message, showDivider }
  })
}

/**
 * Determine whether a message was sent by the guest (vs. the host).
 * Mirrors the direction-field logic in message-bubble.tsx so both
 * components classify the same message the same way.
 */
function isGuestMessage(message: Message) {
  return (
    message.direction === 'guest' ||
    message.direction === 'guest_to_host' ||
    message.direction === 'inbound' ||
    !message.direction
  )
}

/**
 * Renders a single conversation's messages for the new inbox view.
 *
 * Deliberately the mirror image of `src/components/reservation/message-bubble.tsx`:
 * here host messages align right and guest messages align left. This is a new,
 * standalone view - message-bubble.tsx (guest right / host left) stays as-is for
 * its existing callers.
 *
 * Purely presentational: takes the resolved `messages` array as a prop and does
 * not fetch anything itself (the wiring slice feeds it from `useThreadMessages`).
 */
export function ThreadPane({
  messages = [],
  guestDisplayName = 'Guest',
  onResendPendingMessage,
  isSendConfirmationExhausted = false,
  onCheckAgainSendConfirmation,
}: ThreadPaneProps) {
  if (!messages || messages.length === 0) {
    return <EmptyState message="No messages yet for this reservation" />
  }

  return (
    <div className="space-y-3" data-testid="thread-pane">
      {withDayDividers(messages).map(({ message, showDivider }) => {
        const isGuest = isGuestMessage(message)
        const senderName = isGuest ? guestDisplayName || 'Guest' : 'Host'
        const channel = getMessageChannel(message)
        const messageBody = message.bodyPlain || message.body_plain || message.body || ''
        // `useThreadMessages` (GET /admin/threads/:threadId/messages) returns
        // `message_id`/`sent_at`; the embedded-thread shape used elsewhere
        // (e.g. useReservationThread) returns `id`/`date`. Support both so
        // this component works regardless of which the wiring slice feeds it.
        const messageKey = message.message_id || message.id
        const sentAt = message.sent_at || message.date
        const pendingStatus = message.pendingStatus

        return (
          <div key={messageKey}>
            {showDivider && (
              <div
                className="my-3 text-center text-xs font-medium uppercase tracking-wide text-textFaint"
                data-testid="day-divider"
              >
                {dayLabel(sentAt)}
              </div>
            )}
            <div
              className={`flex ${isGuest ? 'justify-start' : 'justify-end'}`}
              data-testid="message-row"
              data-direction={isGuest ? 'guest' : 'host'}
              data-pending-status={pendingStatus}
            >
              <div
                className={`rounded-lg px-4 py-2.5 max-w-[70%] transition-opacity ${
                  isGuest ? 'bg-surface border border-border' : 'bg-surface border border-mint/30'
                } ${pendingStatus === 'sending' ? 'opacity-60' : ''}`}
              >
                <div className="flex items-baseline justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium">{senderName}</span>
                    <Badge variant="secondary" className="text-xs px-1.5 py-0 h-4">
                      {channel}
                    </Badge>
                  </div>
                  {sentAt && (
                    <span className="text-xs text-textMuted whitespace-nowrap">
                      {formatISODateTime(sentAt)}
                    </span>
                  )}
                </div>
                {messageBody && (
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">
                    <LinkifiedText text={messageBody} />
                  </div>
                )}
                {pendingStatus && (
                  <div
                    className="mt-1.5 flex items-center gap-1.5 text-xs"
                    data-testid="pending-send-status"
                  >
                    {pendingStatus === 'sending' && (
                      <span className="flex items-center gap-1 text-textMuted">
                        <Clock className="h-3 w-3" />
                        Sending…
                      </span>
                    )}
                    {pendingStatus === 'sent' && (
                      <>
                        <span className="flex items-center gap-1 text-textMuted">
                          <Check className="h-3 w-3" />
                          Sent
                        </span>
                        {isSendConfirmationExhausted && onCheckAgainSendConfirmation && (
                          <button
                            type="button"
                            onClick={onCheckAgainSendConfirmation}
                            className="text-textMuted underline hover:text-text"
                          >
                            Check again
                          </button>
                        )}
                      </>
                    )}
                    {pendingStatus === 'failed' && (
                      <>
                        <span className="flex items-center gap-1 text-red-500">
                          <AlertCircle className="h-3 w-3" />
                          Failed to send
                        </span>
                        {message.pendingIdempotencyKey && onResendPendingMessage && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-5 px-1.5 text-xs text-red-500 hover:text-red-600"
                            onClick={() => onResendPendingMessage(message.pendingIdempotencyKey!)}
                          >
                            Resend
                          </Button>
                        )}
                      </>
                    )}
                    {pendingStatus === 'ambiguous' && (
                      <>
                        <span className="flex items-center gap-1 text-amber-500">
                          <AlertTriangle className="h-3 w-3" />
                          Delivery unknown - check Uplisting before resending
                        </span>
                        {message.pendingIdempotencyKey && onResendPendingMessage && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-5 px-1.5 text-xs text-amber-600 hover:text-amber-700"
                            onClick={() => onResendPendingMessage(message.pendingIdempotencyKey!)}
                          >
                            Resend anyway (may duplicate)
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
